import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  update: (userId, data) => api.put(`/users/${userId}`, data),
  delete: (userId) => api.delete(`/users/${userId}`),
};

// Departments API
export const departmentsAPI = {
  getAll: () => api.get('/departments'),
  create: (data) => api.post('/departments', data),
  delete: (deptId) => api.delete(`/departments/${deptId}`),
};

// ISO Controls API
export const controlsAPI = {
  getAll: () => api.get('/controls'),
  create: (data) => api.post('/controls', data),
};

// Audit Plans API
export const auditsAPI = {
  getAll: () => api.get('/audits'),
  getOne: (auditId) => api.get(`/audits/${auditId}`),
  create: (data) => api.post('/audits', data),
  update: (auditId, data) => api.put(`/audits/${auditId}`, data),
  delete: (auditId) => api.delete(`/audits/${auditId}`),
};

// Audit Observations API
export const observationsAPI = {
  getAll: (params) => api.get('/observations', { params }),
  create: (data) => api.post('/observations', data),
  update: (obsId, data) => api.put(`/observations/${obsId}`, data),
};

// Evidence API
export const evidenceAPI = {
  getByObservation: (obsId) => api.get(`/evidence/${obsId}`),
  upload: (obsId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/evidence/${obsId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Findings API
export const findingsAPI = {
  getAll: (params) => api.get('/findings', { params }),
  create: (data) => api.post('/findings', data),
  update: (findingId, data) => api.put(`/findings/${findingId}`, data),
};

// CAPA API
export const capaAPI = {
  getAll: (params) => api.get('/capa', { params }),
  create: (data) => api.post('/capa', data),
  update: (capaId, data) => api.put(`/capa/${capaId}`, data),
};

// Notifications API
export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  markRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getDepartmentScores: () => api.get('/dashboard/department-scores'),
};

// Reports API
export const reportsAPI = {
  getAuditReport: (auditId) => api.get(`/reports/audit/${auditId}`),
  getFindingsRegister: () => api.get('/reports/findings'),
};

// Seed API
export const seedAPI = {
  seed: () => api.post('/seed'),
};

export default api;
